import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import {
  type DashboardExperienceOrderResponse,
  type DashboardExperienceOrderSkipDiscountResponse,
} from '@library/api-dashboard-experience'

type OrderTileTelemetryAction =
  | 'restoreOrder'
  | 'skipOrder'
  | 'fetchSkipDiscount'
  | 'applySkipDiscount'
  | 'updateDeliveryDate'

const getStatusCode = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const status = (error as { status?: unknown }).status

    if (typeof status === 'number') {
      return status
    }
  }

  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string') {
      const statusMatch = message.match(/status=(\d+)|responded with (\d+)/)
      const matchedStatus = statusMatch?.[1] ?? statusMatch?.[2]

      if (matchedStatus) {
        return Number(matchedStatus)
      }
    }
  }

  return undefined
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : JSON.stringify(error)

export const useOrderTileTelemetryTracking = ({
  order,
}: {
  order: DashboardExperienceOrderResponse
}) => {
  const buildTelemetryContext = ({
    actionType,
    extra = {},
  }: {
    actionType: OrderTileTelemetryAction
    extra?: Record<string, unknown>
  }) => ({
    page: 'MyHomePage' as const,
    actionType,
    orderId: order.id,
    orderState: order.analytics.order_state,
    orderPhase: order.analytics.order_phase,
    deliveryDayId: order.analytics.delivery_day_id,
    deliverySlotId: order.analytics.delivery_slot_id,
    deliveryDate: order.analytics.delivery_date,
    periodId: order.analytics.period_id,
    portionSize: order.analytics.portion_size,
    ...extra,
  })

  const trackTelemetryForRestoreOrderSuccess = () => {
    const context = buildTelemetryContext({
      actionType: 'restoreOrder',
    })

    datadogRum.addAction('my_home_restore_order_success', context)
    datadogLogs.logger.info('MyHomePage restore order succeeded', {
      ...context,
      restoreOutcome: 'success',
    })
  }

  const trackTelemetryForRestoreOrderError = (error: unknown) => {
    const context = buildTelemetryContext({
      actionType: 'restoreOrder',
    })
    const errorMessage = `Error restoring order in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error('MyHomePage restore order failed', {
      ...context,
      restoreOutcome: 'error',
      statusCode,
      errorMessage,
    })
    datadogRum.addError(error, {
      ...context,
      statusCode,
      errorMessage,
    })
  }

  const trackTelemetryForSkipOrderSuccess = () => {
    const context = buildTelemetryContext({
      actionType: 'skipOrder',
    })

    datadogRum.addAction('my_home_skip_order_success', context)
    datadogLogs.logger.info('MyHomePage skip order succeeded', {
      ...context,
      skipOutcome: 'success',
    })
  }

  const trackTelemetryForSkipOrderError = (error: unknown) => {
    const context = buildTelemetryContext({
      actionType: 'skipOrder',
    })
    const errorMessage = `Error skipping order in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error('MyHomePage skip order failed', {
      ...context,
      skipOutcome: 'error',
      statusCode,
      errorMessage,
    })
    datadogRum.addError(error, {
      ...context,
      statusCode,
      errorMessage,
    })
    datadogRum.addAction('my_home_skip_order_api_error', {
      ...context,
      skipOutcome: 'error',
      statusCode,
    })
  }

  const trackTelemetryForSkipDiscountOfferAvailable = (
    skipDiscountOffer: DashboardExperienceOrderSkipDiscountResponse,
  ) => {
    const context = buildTelemetryContext({
      actionType: 'fetchSkipDiscount',
      extra: {
        isExistingDiscount: skipDiscountOffer.isExistingDiscount,
        promoCode: skipDiscountOffer.promoCode,
      },
    })

    datadogRum.addAction('my_home_order_skip_discount_offer_available', context)
    datadogLogs.logger.info('MyHomePage order skip discount offer available', {
      ...context,
      fetchSkipDiscountOutcome: 'offerAvailable',
    })
  }

  const trackTelemetryForSkipDiscountOfferUnavailable = () => {
    const context = buildTelemetryContext({
      actionType: 'fetchSkipDiscount',
    })

    datadogRum.addAction('my_home_order_skip_discount_offer_unavailable', context)
    datadogLogs.logger.info('MyHomePage order skip discount offer unavailable', {
      ...context,
      fetchSkipDiscountOutcome: 'offerUnavailable',
    })
  }

  const trackTelemetryForSkipDiscountLookupError = (error: unknown) => {
    const context = buildTelemetryContext({
      actionType: 'fetchSkipDiscount',
    })
    const errorMessage = `Error fetching order skip discount in myHome ${getErrorMessage(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error('MyHomePage order skip discount lookup failed', {
      ...context,
      fetchSkipDiscountOutcome: 'error',
      statusCode,
      errorMessage,
    })
    datadogRum.addError(error, {
      ...context,
      statusCode,
      errorMessage,
    })
    datadogRum.addAction('my_home_order_skip_discount_api_error', {
      ...context,
      fetchSkipDiscountOutcome: 'error',
      statusCode,
    })
  }

  const trackTelemetryForApplySkipDiscountSuccess = ({
    promoCode,
    status,
  }: {
    promoCode: string
    status: string
  }) => {
    const context = buildTelemetryContext({
      actionType: 'applySkipDiscount',
      extra: {
        promoCode,
      },
    })

    datadogRum.addAction('my_home_apply_skip_discount_success', {
      ...context,
      applyPromotionCodeStatus: status,
    })
    datadogLogs.logger.info('MyHomePage apply skip discount succeeded', {
      ...context,
      applySkipDiscountOutcome: 'success',
      applyPromotionCodeStatus: status,
    })
  }

  const trackTelemetryForApplySkipDiscountError = ({
    error,
    promoCode,
  }: {
    error: unknown
    promoCode: string
  }) => {
    const context = buildTelemetryContext({
      actionType: 'applySkipDiscount',
      extra: {
        promoCode,
      },
    })
    const errorMessage = `Error applying skip discount in myHome ${getErrorMessage(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error('MyHomePage apply skip discount failed', {
      ...context,
      applySkipDiscountOutcome: 'error',
      statusCode,
      errorMessage,
    })
    datadogRum.addError(error, {
      ...context,
      statusCode,
      errorMessage,
    })
    datadogRum.addAction('my_home_apply_skip_discount_api_error', {
      ...context,
      applySkipDiscountOutcome: 'error',
      statusCode,
    })
  }

  const trackTelemetryForUpdateDeliveryDateError = ({
    error,
    selectedDeliveryDate,
  }: {
    error: unknown
    selectedDeliveryDate: string
  }) => {
    const context = buildTelemetryContext({
      actionType: 'updateDeliveryDate',
      extra: {
        selectedDeliveryDate,
      },
    })
    const errorMessage = `Error updating delivery date in myHome ${JSON.stringify(error)}`
    const statusCode = getStatusCode(error)

    datadogLogs.logger.error('MyHomePage update delivery date failed', {
      ...context,
      updateDeliveryDateOutcome: 'error',
      statusCode,
      errorMessage,
    })
    datadogRum.addError(error, {
      ...context,
      statusCode,
      errorMessage,
    })
    datadogRum.addAction('my_home_update_delivery_date_api_error', {
      ...context,
      updateDeliveryDateOutcome: 'error',
      statusCode,
    })
  }

  return {
    trackTelemetryForApplySkipDiscountError,
    trackTelemetryForApplySkipDiscountSuccess,
    trackTelemetryForRestoreOrderError,
    trackTelemetryForRestoreOrderSuccess,
    trackTelemetryForSkipDiscountLookupError,
    trackTelemetryForSkipDiscountOfferAvailable,
    trackTelemetryForSkipDiscountOfferUnavailable,
    trackTelemetryForSkipOrderError,
    trackTelemetryForSkipOrderSuccess,
    trackTelemetryForUpdateDeliveryDateError,
  }
}
